import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSuccessionTheme } from '../SuccessionThemeProvider';

const UPDATE_PARTIAL_SALE = gql`
  mutation UpdatePartialSale($id: ID!, 
                              $description: String, 
                              $number_of_clients: Int,
                              $assets_under_management: Float,
                              $fee_based_revenue: Float,
                              $commission_revenue: Float,
                              $trailer_revenue: Float,
                              $direct_revenue: Float,
                              $asking_price: Float,
                              $sale_status: String,
                              $reason_for_close: String) {
    updatePartialSale(  id: $id, 
                        description: $description,
                        number_of_clients: $number_of_clients,
                        assets_under_management: $assets_under_management,
                        fee_based_revenue: $fee_based_revenue,
                        commission_revenue: $commission_revenue,
                        trailer_revenue: $trailer_revenue,
                        direct_revenue: $direct_revenue,
                        asking_price: $asking_price,
                        sale_status: $sale_status,
                        reason_for_close: $reason_for_close ) {
      id
      description
      number_of_clients
      assets_under_management
      fee_based_revenue
      asking_price
      sale_status
      reason_for_close
      commission_revenue
      trails_revenue
      direct_revenue
    }
  }
`;

const PartialSaleEditForm = ({ listing, onCancel, onSave, company }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [updatePartialSale, { loading }] = useMutation(UPDATE_PARTIAL_SALE);


  
  const [formData, setFormData] = useState({
    description: listing.description || '',
    number_of_clients: listing.number_of_clients || 0,
    assets_under_management: listing.assets_under_management || 0,
    fee_based_revenue: listing.fee_based_revenue || 0,
    commission_revenue: listing.commission_revenue || 0,
    trails_revenue: listing.trails_revenue || 0,
    direct_revenue: listing.direct_revenue || 0,
    asking_price: listing.asking_price || 0,
    sale_status: listing.sale_status || 'open',
  });

  const [isNegotiable, setIsNegotiable] = useState(
    !listing.asking_price || listing.asking_price === 0 || parseFloat(listing.asking_price) === 0
  );
  const [errors, setErrors] = useState({});
  
  // Modal state for closing reason
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [reasonForClose, setReasonForClose] = useState(listing.reason_for_close || '');
  const [pendingStatus, setPendingStatus] = useState('');

  const partialSalesPropertyCasualty = company?.feature_types?.includes('partial_sales_property_casualty') || false;
  const hideProductSalesPurchasePrice = company?.feature_types?.includes('hide_partial_sales_purchase_price') || false;

  const inputClasses = "w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent bg-succession-bg-secondary text-succession-text-primary transition-colors duration-300";
  const labelClasses = "block text-sm font-medium mb-2 text-succession-text-primary transition-colors duration-300";

  const statusOptions = [
    { value: 'new', label: intl.formatMessage({ id: 'succession.partial_sales.edit_form.status_draft' }) },
    { value: 'open', label: intl.formatMessage({ id: 'succession.partial_sales.edit_form.status_published' }) },
    { value: 'closed', label: intl.formatMessage({ id: 'succession.partial_sales.edit_form.status_closed' }) }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleNegotiableChange = (checked) => {
    setIsNegotiable(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, asking_price: 0 }));
    }
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === 'closed') {
      // Show modal for closing reason
      setPendingStatus(newStatus);
      setShowCloseModal(true);
    } else {
      // Update status immediately for non-closed statuses
      handleInputChange('sale_status', newStatus);
    }
  };

  const handleCloseWithReason = () => {
    if (reasonForClose.trim()) {
      handleInputChange('sale_status', 'closed');
      setShowCloseModal(false);
    }
  };



  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.description.trim()) {
      newErrors.description = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_description_required' });
    }
    if (formData.number_of_clients <= 0) {
      newErrors.number_of_clients = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_clients_required' });
    }

    // Revenue validation - at least one revenue field must be positive
    const totalRevenue = (parseFloat(formData.fee_based_revenue) || 0) +
                        (parseFloat(formData.commission_revenue) || 0) +
                        (parseFloat(formData.trails_revenue) || 0) +
                        (parseFloat(formData.direct_revenue) || 0);

    if (totalRevenue <= 0) {
      newErrors.general = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_revenue_required' });
    }

    // Individual revenue fields cannot be negative
    if (formData.fee_based_revenue < 0) newErrors.fee_based_revenue = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_revenue_negative' });
    if (formData.commission_revenue < 0) newErrors.commission_revenue = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_revenue_negative' });
    if (formData.trails_revenue < 0) newErrors.trails_revenue = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_revenue_negative' });
    if (formData.direct_revenue < 0) newErrors.direct_revenue = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_revenue_negative' });
    if (formData.asking_price < 0) newErrors.asking_price = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_price_negative' });
    if (formData.assets_under_management < 0) newErrors.assets_under_management = intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_assets_negative' });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const result = await updatePartialSale({
        variables: {
          id: listing.id,
          description: formData.description,
          number_of_clients: parseInt(formData.number_of_clients),
          assets_under_management: parseFloat(formData.assets_under_management),
          fee_based_revenue: parseFloat(formData.fee_based_revenue),
          commission_revenue: parseFloat(formData.commission_revenue),
          trailer_revenue: parseFloat(formData.trails_revenue),
          direct_revenue: parseFloat(formData.direct_revenue),
          asking_price: parseFloat(formData.asking_price),
          sale_status: formData.sale_status,
          reason_for_close: formData.sale_status === 'closed' ? reasonForClose : null
        }
      });

      if (result.data?.updatePartialSale) {
        onSave(result.data.updatePartialSale);
      }
    } catch (error) {
      console.error('Error updating partial sale:', error);
      setErrors({ general: intl.formatMessage({ id: 'succession.partial_sales.edit_form.error_update_failed' }) });
    }
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.edit_form.title' })}
          </h3>
          <p className="text-xs text-succession-text-secondary mt-1">
            {intl.formatMessage({ id: 'succession.partial_sales.edit_form.subtitle' })}
          </p>
        </div>
        <button
          onClick={onCancel}
          className="text-succession-text-secondary hover:text-succession-text-primary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 bg-succession-error-bg border border-succession-error rounded-lg text-succession-error text-sm">
          {errors.general}
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-4">
        {/* Status */}
        <div>
          <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>{intl.formatMessage({ id: 'succession.partial_sales.edit_form.status_label' })}</label>
          <select
            value={formData.sale_status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className={inputClasses}
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>{intl.formatMessage({ id: 'succession.partial_sales.edit_form.description_label' })}</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className={`${inputClasses} h-40 resize-none`}
            placeholder={intl.formatMessage({ id: 'succession.partial_sales.edit_form.description_placeholder' })}
            maxLength={250}
          />
          {errors.description && <p className="text-succession-error text-xs mt-1">{errors.description}</p>}
          <p className="text-xs text-succession-text-secondary mt-1">{formData.description.length}/250</p>
        </div>

        {/* Number of Clients */}
        <div>
          <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>{intl.formatMessage({ id: 'succession.partial_sales.edit_form.number_of_clients_label' })}</label>
          <input
            type="number"
            value={formData.number_of_clients}
            onChange={(e) => handleInputChange('number_of_clients', e.target.value)}
            className={inputClasses}
            min="0"
          />
          {errors.number_of_clients && <p className="text-succession-error text-xs mt-1">{errors.number_of_clients}</p>}
        </div>

        {/* Assets Under Management */}
        <div>
          <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>
            {partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.edit_form.total_premium_label' }) : intl.formatMessage({ id: 'succession.partial_sales.edit_form.assets_under_management_label' })}
          </label>
          <input
            type="number"
            value={formData.assets_under_management}
            onChange={(e) => handleInputChange('assets_under_management', e.target.value)}
            className={inputClasses}
            min="0"
            step="0.01"
          />
          {errors.assets_under_management && <p className="text-succession-error text-xs mt-1">{errors.assets_under_management}</p>}
        </div>

        {/* Fee Based Revenue */}
        <div>
          <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>
            {partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.edit_form.recurring_commission_label' }) : intl.formatMessage({ id: 'succession.partial_sales.edit_form.fee_based_revenue_label' })}
          </label>
          <input
            type="number"
            value={formData.fee_based_revenue}
            onChange={(e) => handleInputChange('fee_based_revenue', e.target.value)}
            className={inputClasses}
            min="0"
            step="0.01"
          />
          {errors.fee_based_revenue && <p className="text-succession-error text-xs mt-1">{errors.fee_based_revenue}</p>}
        </div>

        {/* Commission Revenue */}
        <div>
          <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>
            {partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.partial_sales.edit_form.new_commission_label' }) : intl.formatMessage({ id: 'succession.partial_sales.edit_form.commission_revenue_label' })}
          </label>
          <input
            type="number"
            value={formData.commission_revenue}
            onChange={(e) => handleInputChange('commission_revenue', e.target.value)}
            className={inputClasses}
            min="0"
            step="0.01"
          />
          {errors.commission_revenue && <p className="text-succession-error text-xs mt-1">{errors.commission_revenue}</p>}
        </div>


        {/* Trails Revenue - Hidden for Property & Casualty */}
        {!partialSalesPropertyCasualty && (
          <div>
            <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>{intl.formatMessage({ id: 'succession.partial_sales.edit_form.trails_revenue_label' })}</label>
            <input
              type="number"
              value={formData.trails_revenue}
              onChange={(e) => handleInputChange('trails_revenue', e.target.value)}
              className={inputClasses}
              min="0"
              step="0.01"
            />
            {errors.trails_revenue && <p className="text-succession-error text-xs mt-1">{errors.trails_revenue}</p>}
          </div>
        )}

        {/* Direct Revenue - Hidden for Property & Casualty */}
        {!partialSalesPropertyCasualty && (
          <div>
            <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>{intl.formatMessage({ id: 'succession.partial_sales.edit_form.direct_revenue_label' })}</label>
            <input
              type="number"
              value={formData.direct_revenue}
              onChange={(e) => handleInputChange('direct_revenue', e.target.value)}
              className={inputClasses}
              min="0"
              step="0.01"
            />
            {errors.direct_revenue && <p className="text-succession-error text-xs mt-1">{errors.direct_revenue}</p>}
          </div>
        )}

        {/* Asking Price */}
        {!hideProductSalesPurchasePrice && (
          <div>
            <label className={labelClasses} style={{paddingBottom: '0px', paddingTop: '0px'}}>{intl.formatMessage({ id: 'succession.partial_sales.edit_form.asking_price_label' })}</label>
            <input
              type="number"
              value={formData.asking_price}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                handleInputChange('asking_price', value);
                // Check if price is 0, 0.0, or nil (empty/NaN)
                setIsNegotiable(!value || value === 0);
              }}
              className={inputClasses}
              min="0"
              step="0.01"
              disabled={isNegotiable}
            />
            {errors.asking_price && <p className="text-succession-error text-xs mt-1">{errors.asking_price}</p>}
            <div className="mt-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isNegotiable}
                  onChange={(e) => handleNegotiableChange(e.target.checked)}
                  className="mr-2 h-4 w-4 border-succession-border rounded focus:ring-2 focus:ring-succession-primary focus:ring-offset-2 hover:border-succession-primary transition-colors"
                  style={{
                    accentColor: 'var(--succession-primary)',
                    backgroundColor: isNegotiable ? 'var(--succession-primary)' : 'transparent'
                  }}
                />
                <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.partial_sales.edit_form.price_negotiable' })}</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3 mt-6 pt-4 border-t border-succession-border">
        <button
          onClick={onCancel}
          className="flex-1 py-2 px-4 bg-succession-secondary text-white rounded-lg hover:opacity-80 transition-all"
        >
          {intl.formatMessage({ id: 'succession.partial_sales.edit_form.cancel' })}
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="flex-1 py-2 px-4 bg-succession-primary text-white rounded-lg hover:opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? intl.formatMessage({ id: 'succession.partial_sales.edit_form.saving' }) : intl.formatMessage({ id: 'succession.partial_sales.edit_form.save' })}
        </button>
      </div>

      {/* Closing Reason Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-succession-bg-primary rounded-lg p-6 max-w-md w-full mx-4 border border-succession-border">
            <h2 className="text-xl font-semibold mb-4 text-succession-text-primary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.edit_form.close_modal_title' })}
            </h2>
            <p className="text-sm mb-4 text-succession-text-secondary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.edit_form.close_modal_description' })}
            </p>
            <textarea
              value={reasonForClose}
              onChange={(e) => setReasonForClose(e.target.value)}
              className="w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent bg-succession-bg-secondary text-succession-text-primary transition-colors duration-300"
              rows={4}
              placeholder={intl.formatMessage({ id: 'succession.partial_sales.edit_form.close_modal_placeholder' })}
              required
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowCloseModal(false);
                  setPendingStatus('');
                  setReasonForClose(listing.reason_for_close || '');
                }}
                className="px-4 py-2 bg-succession-secondary text-white rounded hover:opacity-80 transition-all"
              >
                {intl.formatMessage({ id: 'succession.partial_sales.edit_form.cancel' })}
              </button>
              <button
                onClick={handleCloseWithReason}
                disabled={!reasonForClose.trim()}
                className={`px-4 py-2 rounded transition-all ${
                  !reasonForClose.trim()
                    ? 'bg-succession-bg-secondary text-succession-text-muted cursor-not-allowed border border-succession-border'
                    : 'bg-succession-error text-white hover:opacity-80'
                }`}
              >
                {intl.formatMessage({ id: 'succession.partial_sales.edit_form.close_sale' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartialSaleEditForm;